import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { MaskText } from "./Reveal";
import { scrollToId } from "@/hooks/useLenis";
import { useReserve } from "./Reserve";

const EASE = [0.22, 1, 0.36, 1];

const fade = (delay) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, ease: EASE, delay },
});

export const Hero = () => {
  const ref = useRef(null);
  const { open } = useReserve();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);

  return (
    <section ref={ref} id="top" className="relative bg-cream overflow-hidden">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 pt-28 sm:pt-36 lg:pt-40 pb-16 lg:pb-20">
        <motion.div {...fade(0.1)} className="flex items-center gap-3 mb-8 sm:mb-10">
          <span className="h-px w-10 bg-cognac" />
          <span className="font-sans text-[11px] sm:text-xs uppercase tracking-[0.3em] text-cognac">
            From Brian Smith, Founder of UGG
          </span>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-8 items-start">
          <motion.div style={{ y: textY }} className="lg:col-span-7">
            <h1 className="font-serif text-ocean tracking-[-0.02em] leading-[0.98] text-[2.9rem] sm:text-[4.2rem] lg:text-[5.4rem]">
              <MaskText
                lines={["Create your business", "roadmap ", <span key="d">in <em className="italic text-cognac">7 days.</em></span>]}
                delay={0.15}
                onLoad
              />
            </h1>

            <motion.p {...fade(0.6)} className="mt-7 font-serif italic text-xl sm:text-2xl lg:text-[1.7rem] text-ocean/85 leading-snug max-w-2xl">
              Know where you are. Decide where you're going. Build the roadmap for what comes next.
            </motion.p>

            <motion.div {...fade(0.72)} className="mt-8 grid sm:grid-cols-2 gap-6 max-w-2xl font-sans text-base text-ink/72 leading-relaxed">
              <p>
                <span className="text-ocean font-medium">Your business is going to change.</span> The
                market will change. Your customers will teach you things you didn't know.
                Opportunities will appear. Plans will go sideways. And what made perfect sense a
                year ago may make absolutely no sense today.
              </p>
              <p>
                <em>Create Your Business Roadmap in 7 Days</em> gives you a practical system for
                stepping out of the noise, figuring out what stage your business is actually in,
                and deciding what deserves your attention over the next 12 months.
              </p>
            </motion.div>

            <motion.div {...fade(0.85)} className="mt-10 flex flex-wrap items-center gap-5">
              <button
                data-testid="hero-reserve-btn"
                onClick={() => open("hero")}
                className="group font-sans text-sm uppercase tracking-[0.18em] bg-ocean text-cream px-8 py-4 hover:bg-cognac transition-colors duration-400"
              >
                Reserve My Spot
                <span className="inline-block ml-3 transition-transform duration-300 group-hover:translate-x-1">→</span>
              </button>
              <button
                data-testid="hero-quiz-btn"
                onClick={() => scrollToId("assessment")}
                className="link-underline font-sans text-sm uppercase tracking-[0.14em] text-ocean"
              >
                What stage is my business in?
              </button>
            </motion.div>

            <motion.p {...fade(1)} data-testid="hero-launch-note" className="mt-10 max-w-md font-sans text-xs sm:text-[13px] text-ink/55 leading-relaxed border-l border-cognac/50 pl-4">
              <span className="uppercase tracking-[0.2em] text-cognac">Launching January 5, 2027</span>
              <br />
              The first 50 founders to join will be invited to a private live kickoff with Brian
              Smith on launch day.
            </motion.p>
          </motion.div>

          <div className="lg:col-span-5">
            <motion.figure
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, ease: EASE, delay: 0.5 }}
              className="relative grain overflow-hidden border border-[var(--line)]"
            >
              <motion.img
                style={{ y: imgY, scale: imgScale }}
                src="/images/brian-stage.png"
                alt="Brian Smith speaking on stage"
                className="w-full h-[52vh] lg:h-[72vh] object-cover object-top img-warm"
              />
              <figcaption className="absolute bottom-0 inset-x-0 z-10 bg-gradient-to-t from-ocean/85 to-transparent p-5">
                <span className="font-sans text-[11px] uppercase tracking-[0.22em] text-cream/90">
                  Brian Smith · Founder of UGG
                </span>
              </figcaption>
            </motion.figure>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="hairline pt-5 pb-2 flex flex-wrap items-center justify-between gap-4 font-sans text-xs uppercase tracking-[0.2em] text-ocean/55">
          <span>Seven days</span>
          <span>One personalized 12-month roadmap</span>
          <span>Built on the lifecycle of a business</span>
          <span>Return whenever things change</span>
        </div>
      </div>
    </section>
  );
};
